<template>
  <div class="-linked-fields-browser" ref="container" :style="browserStyle">
    <wt-text-field
      v-model="treeFilter"
      placeholder="Search..."
      icon="wticon-magnifier"
      class="filter-field"
    />
    <tree
      :data="treeData"
      :filter="treeFilter"
      :options="treeOptions"
      ref="tree"
    >
      <span slot-scope="{ node }" class="viewer-item" :class="[node.data.type]">
        <span v-if="node.hasChildren()" class="viewer-item__key">{{
          node.text
        }}</span>
        <div v-else class="viewer-item__prop">
          <span class="viewer-item__key">{{ node.data.label }}</span>
          <i
            class="wticon wticon-help-alt co-tooltip"
            v-tooltip.extend="{
              title: `Example: '${node.data.objectKey}'`,
              delay: 250,
              placement: 'right',
              wrapperClass: '-break-word',
            }"
          ></i>
        </div>
      </span>
    </tree>
  </div>
</template>

<script>
const compoundRoots = [];
export default {
  props: {
    options: {
      type: Object,
      default() {
        return {
          value: null,
        };
      },
    },
    linkedFields: {
      type: Object,
      default() {
        return {};
      },
    },
    urlsOnly: {
      type: Boolean,
      default: false,
    },
  },
  name: 'LinkedFieldsTagsBrowser',
  data() {
    return {
      treeOptions: {
        multiple: false,
        filter: {
          emptyText: 'No Results...',
        },
      },
      browserStyle: { top: 0, left: 0, display: 'flex', height: '300px' },
      treeData: null,
      treeFilter: null,
    };
  },
  computed: {
    splittedValueKey() {
      return this.splitObjKey(this.options.value);
    },
    splittedValueParentKey() {
      const value = this.splitObjKey(this.options.value);
      if (value) {
        value.splice(-1, 1);
      }
      return value;
    },
  },
  created() {
    this.treeData = this.parser(this.linkedFields);
  },
  mounted() {
    const elTop = this.$el.getBoundingClientRect().top;
    const elHeight = this.$el.getBoundingClientRect().height;
    if (elTop + elHeight > window.innerHeight - 25) {
      this.browserStyle.height = window.innerHeight - elTop - 10 + 'px';
    }
    this.$refs.tree.$on('tree:mounted', () => {
      this.scrollIntoSelectedNode();
    });
    this.$refs.tree.$on('node:selected', (node) => {
      this.emitSelected();
    });
    document.addEventListener('mouseup', this.onDocumentMouseup);
  },
  beforeDestroy() {
    document.removeEventListener('mouseup', this.onDocumentMouseup);
  },
  methods: {
    emitSelected() {
      const nodeTree = this.getSelectedNodeTree();
      this.$emit('selected', {
        value: nodeTree.tree.join('.'),
        text: nodeTree.text,
      });
    },
    scrollIntoSelectedNode() {
      this.$nextTick(() => {
        const expandedEls =
          this.$refs.tree.$el.getElementsByClassName('tree-node selected');
        if (expandedEls.length) {
          expandedEls[0].scrollIntoView({
            block: 'center',
            // inline: 'nearest',
          });
        }
      });
    },
    onDocumentMouseup(event) {
      const isClickOutside = !this.$refs.container.contains(event.target);
      const isTagClick = event.target.classList.contains('-tag');

      if (isClickOutside && !isTagClick) {
        this.$emit('cancel');
      }
    },
    getSelectedNodeTree() {
      const selectedNodes = this.$refs.tree.tree.selectedNodes;
      const text = selectedNodes[0].data.objectKey;
      const tree = [];

      if (selectedNodes) {
        let node = selectedNodes[0];
        while (node) {
          tree.unshift(node.data.text);
          node = node.parent;
        }
      }

      return { tree, text };
    },
    matchExpandedKeys(checkeArr, targetArr) {
      if (!targetArr || !checkeArr) {
        return;
      }
      let match = true;
      checkeArr.forEach((cv, ck) => {
        if (targetArr[ck] !== cv) {
          match = false;
        }
      });
      return match;
    },

    splitObjKey(value) {
      if (!value) {
        return;
      }
      const valueNode = value.split('.');
      return valueNode;
    },

    isString(value) {
      return 'string' == typeof value;
    },

    isNaN(value) {
      return value !== value;
    },

    isBoolean(value) {
      return 'boolean' == typeof value;
    },

    isNumber(value) {
      return 'number' == typeof value;
    },

    isArray(value) {
      return Array.isArray(value);
    },

    isValue(value) {
      return !this.isArray(value) && !this.isPlainObject(value);
    },

    isPlainObject(value) {
      function isObject(obj) {
        return (
          obj != null && typeof obj === 'object' && Array.isArray(obj) === false
        );
      }

      if (
        !isObject(value) ||
        !value.constructor ||
        !value.constructor.prototype
      ) {
        return false;
      }

      return !!Object.getOwnPropertyDescriptor(
        value.constructor.prototype,
        'isPrototypeOf'
      );
    },

    isIterable(value) {
      return this.isArray(value) || this.isPlainObject(value);
    },

    map(obj, previousKeyHierarchy) {
      return Object.keys(obj)
        .sort((a, b) => {
          if (_.isNumeric(a) && _.isNumeric(b)) {
            return parseFloat(a) - parseFloat(b);
          } else {
            return `${a}`.localeCompare(`${b}`);
          }
        })
        .map((key) => {
          return this.transformObject(obj[key], key, previousKeyHierarchy);
        });
    },

    transformObject(prop, key, previousKeyHierarchy) {
      let isCompoundProperty = false;
      const keyHierarchy = (previousKeyHierarchy || []).slice();
      keyHierarchy.push(key);

      if (
        _.isObject(prop) &&
        prop._attributes &&
        prop._attributes.type === 'compound'
      ) {
        const compoundRoot = _.clone(keyHierarchy);
        compoundRoot.push('list');
        compoundRoots.push(compoundRoot);
      }

      _.each(compoundRoots, (compoundRoot) => {
        if (_.isEqual(compoundRoot, previousKeyHierarchy)) {
          isCompoundProperty = true;
        }
      });

      const obj = {
        text: key,
        state: {
          selected: false,
          expanded: false,
        },
      };

      if (key === '_attributes') {
        obj.state.visible = false;
      }

      if (!isCompoundProperty && this.isIterable(prop)) {
        if (this.matchExpandedKeys(keyHierarchy, this.splittedValueParentKey)) {
          obj.state.expanded = true;
        }
        obj.children = this.map(prop, keyHierarchy);
        obj.data = {
          type: this.isArray(prop)
            ? 'array'
            : this.isPlainObject(prop)
            ? 'object'
            : 'unknown',
        };
        obj.state.selectable = isCompoundProperty ? true : false;
      } else {
        if (this.urlsOnly && !/https?:\/\//.test(prop)) {
          obj.state.disabled = true;
        }

        if (
          JSON.stringify(this.splittedValueKey) === JSON.stringify(keyHierarchy)
        ) {
          obj.state.selected = true;
        }

        obj.data = {
          label: isCompoundProperty ? parseInt(key) : key,
          objectKey: _.isObject(prop) ? JSON.stringify(prop) : `${prop}`,
          type: this.getType(prop),
        };
      }

      return obj;
    },

    getType(value) {
      if (this.isNaN(value)) {
        return 'viewer-type--nan';
      }

      if (this.isString(value)) {
        return 'viewer-type--string';
      }

      if (this.isNumber(value)) {
        return 'viewer-type--number';
      }

      if (this.isBoolean(value)) {
        return 'viewer-type--boolean';
      }

      if (null === value) {
        return 'viewer-type--null';
      }
    },

    parser(obj) {
      return this.map(obj);
    },
  },
  watch: {
    options() {
      this.scrollIntoSelectedNode();
    },
  },
};
</script>

<style lang="scss">
.-linked-fields-browser {
  position: absolute;
  width: 100%;
  height: 300px;
  right: 0;
  overflow-y: auto;
  overflow-x: auto;
  background: rgb(255, 255, 255);
  display: flex;
  flex-direction: column;
  z-index: 999999999999;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

  .filter-field {
    width: 100%;
    .wticon {
      font-size: 16px;
      transform: none;
      cursor: none;
      position: relative;
      position: absolute;
      top: 10px;
      left: 10px;
      width: 16px;
    }
    input {
      padding: 0 10px 0 40px !important;
      border-bottom: 1px solid rgba(0, 0, 0, 0.2);
    }
  }

  .tree {
    flex-grow: 1;
    ul,
    ol {
      margin-bottom: 0;
      height: auto !important;
      li {
        border: none !important;
        height: auto !important;
        width: auto !important;
        &:hover {
          background-color: inherit !important;
        }
        .tree-content {
          .wticon-help-alt {
            display: none;
          }
          &:hover {
            .wticon-help-alt {
              display: inline;
            }
          }
        }
      }
    }
    .tree-root {
      padding: 0 !important;
    }
    .tree-node {
      &.matched {
        //background: yellow;
      }
    }
    .tree-anchor {
      color: rgba(0, 0, 0, 0.75);
    }
  }
  .tree-filter-empty {
    padding-left: 10px;
  }
}
</style>
